import moment from 'moment-timezone';
import { PercentageByTariff, Session, TransactionStopReason } from '../../app/ApiGen';
import { NormalisedSession } from '../../views/Organisations/Organisation/Reports/MonthlyUseByCharger/types';
import { chargePointHasAttributes, convertToKwh, locationHasAttributes } from '../format';
import { getAddressAsString } from '../locations/address';
import { roundTo2DecimalPlaces } from '../number';
import { Normalised } from '../request';

export const SOLAR_EXPORT_TARIFF_NAME = 'Solar';

export const AsbReimbursementRate = 0.4;

type SessionCost = { amount: string; currency: string };

export type TableSessionModel = Pick<
  NormalisedSession,
  'id' | 'evseId' | 'connectorId' | 'chargePointReferenceId' | 'startDate' | 'endDate'
> & {
  chargePointRefId?: string;
  chargePointName: string;
  locationName?: string;
  locationAddress?: string;
  token?: { id?: string; uid?: string; name?: string };
  chargingStarted?: string;
  chargingStopped?: string;
  duration?: number;
  usage?: string;
  electricityCost?: SessionCost;
  carbonOffset?: string;
  stopReason?: TransactionStopReason;
  totalCarbonUsage?: number;
  cost?: SessionCost;
  reimbursementFigure?: SessionCost;
  transactionId?: string;
};

function gridEnergyProportion(session: Normalised<Session>): number {
  const distributionByTariff: PercentageByTariff | undefined = session.energyUsage?.distributionByTariff;

  if (!distributionByTariff) {
    return 1;
  }

  const percentage = Object.keys(distributionByTariff).reduce<number>((currentPercentage, tariffType) => {
    if (tariffType === SOLAR_EXPORT_TARIFF_NAME) {
      return currentPercentage;
    }
    return currentPercentage + distributionByTariff[tariffType];
  }, 0);

  return percentage / 100;
}

export function costOrLegacyCostOrDefault(args: { session: Normalised<Session> }): number {
  const { session } = args;
  return Number(session.cost?.total ?? session.totalCost?.amount ?? 0);
}

export function calculateReimbursementRate(args: { session: Normalised<Session> }): number | undefined {
  const { session } = args;
  const { chargingConfiguration, energyUsage } = session;

  const importTariffs = chargingConfiguration?.tariffs.import ?? {};
  const tariffNames = Object.keys(importTariffs);

  if (tariffNames.length === 1) {
    const [firstTariffName] = tariffNames;
    const firstTariff = importTariffs[firstTariffName];
    if (firstTariff.amount === `${AsbReimbursementRate}`) {
      return costOrLegacyCostOrDefault({ session });
    }
  }

  const totalEnergyUsage = energyUsage?.total ?? 0;
  const gridPercentage = gridEnergyProportion(session);
  const gridUsage = convertToKwh(totalEnergyUsage * gridPercentage);
  return gridUsage * AsbReimbursementRate;
}

function toReimbursementFigure(args: {
  session: Normalised<Session>;
  forAsb?: boolean;
}): TableSessionModel['reimbursementFigure'] {
  const { forAsb, session } = args;
  const { chargingConfiguration, electricityCost, energyUsage } = session;
  const amount = calculateReimbursementRate({ session });
  const currency = chargingConfiguration?.currency ?? electricityCost?.currency ?? 'NZD';

  if (!forAsb || energyUsage === undefined || amount === undefined) {
    return undefined;
  }

  return { amount: `${roundTo2DecimalPlaces(amount)}`, currency };
}

export function toTableSessionModel(args: { session: Normalised<Session>; forAsb?: boolean }): TableSessionModel {
  const { forAsb, session } = args;
  const {
    chargePoint,
    chargingConfiguration,
    connectorId,
    cost,
    electricityCost,
    endDate,
    evseId,
    id,
    location,
    startDate,
    token,
    totalCarbonUsage,
    totalCost,
    totalPowerUsage,
    transaction,
  } = session;
  const currency = chargingConfiguration?.currency ?? electricityCost?.currency ?? 'NZD';
  return {
    carbonOffset: totalPowerUsage ? ((totalPowerUsage / 1000) * 0.3).toFixed(2) : undefined,
    chargePointName: chargePointHasAttributes(chargePoint) ? chargePoint.name : '',
    chargePointRefId: chargePointHasAttributes(chargePoint) ? chargePoint.referenceId : '',
    connectorId,
    cost: {
      amount: cost?.total ?? totalCost?.amount ?? '0',
      currency,
    },
    duration: endDate ? moment(endDate).diff(moment(startDate)) : undefined,
    endDate,
    evseId,
    id,
    locationAddress: locationHasAttributes(location) ? getAddressAsString(location.address) : undefined,
    locationName: locationHasAttributes(location) ? location.name : '',
    reimbursementFigure: toReimbursementFigure({ forAsb, session }),
    startDate,
    stopReason: transaction?.reason,
    token: { id: token?.tokenId, name: token?.tokenName },
    totalCarbonUsage,
    usage: totalPowerUsage ? (totalPowerUsage / 1000).toFixed(2) : undefined,
    transactionId: transaction?.id.toString(),
  };
}
